import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SocialProofStats {
  votesToday: number;
  totalSaves: number;
  totalPolls: number;
  totalUsers: number;
  autoBoostRate: number;
  autoBoostCap: number;
  isLoading: boolean;
}

export function useSocialProof(): SocialProofStats {
  const { data: realStats } = useQuery({
    queryKey: ['social-proof-stats'],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_social_proof_stats');
      if (error) throw error;
      return data as {
        votes_today: number;
        total_saves: number;
        total_polls: number;
        total_users: number;
      };
    },
    staleTime: 5 * 60 * 1000, // 5 min
    gcTime: 10 * 60 * 1000,
  });

  const { data: boosts } = useQuery({
    queryKey: ['social-proof-boosts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_proof_settings' as any)
        .select('votes_boost, saves_boost, polls_boost, users_boost, auto_boost_rate, auto_boost_cap')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return data as any as {
        votes_boost: number;
        saves_boost: number;
        polls_boost: number;
        users_boost: number;
        auto_boost_rate: number;
        auto_boost_cap: number;
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    votesToday: (realStats?.votes_today || 0) + (boosts?.votes_boost || 0),
    totalSaves: (realStats?.total_saves || 0) + (boosts?.saves_boost || 0),
    totalPolls: (realStats?.total_polls || 0) + (boosts?.polls_boost || 0),
    totalUsers: (realStats?.total_users || 0) + (boosts?.users_boost || 0),
    autoBoostRate: boosts?.auto_boost_rate || 0,
    autoBoostCap: boosts?.auto_boost_cap || 500,
    isLoading: !realStats,
  };
}
